"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { Menu, X, ChevronDown } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)
  const [openMobileSubMenu, setOpenMobileSubMenu] = useState<string | null>(null)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (mobileMenuOpen && !target.closest(".mobile-menu") && !target.closest(".menu-button")) {
        setMobileMenuOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [mobileMenuOpen])

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "auto"
    }
    return () => {
      document.body.style.overflow = "auto"
    }
  }, [mobileMenuOpen])

  const navItems = [
    // { name: "About", href: "#about" },
    {
      name: "Services",
      href: "#services",
      subItems: [
        { name: "Annual Report", href: "/services/annual-report-design" },
        { name: "Sustainability Report", href: "/services/sustainability-report-design" },
        { name: "ESG report", href: "/services/esg-report-design" },
        { name: "Integrated Report", href: "/services/integrated-report-design" },
      ],
    },
    { name: "Portfolio", href: "/portfolio" },
    { name: "Process", href: "/process" },
    { name: "Testimonial", href: "/testimonial" },
    { name: "Contact", href: "/contact" },
    // { name: "Blog", href: "/blog" },
  ]

  const dropdownVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeInOut" } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2, ease: "easeInOut" } },
  }

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 text-black ${isScrolled ? "bg-white/90 backdrop-blur-md shadow-lg py-3" : "bg-gray-100 py-4"
        }`}
    >
      <div className="container mx-auto px-4 flex items-center justify-between">
        <Link href="/" className="flex items-center z-20">
          <motion.div
            className="relative h-8 md:h-10 font-bold text-3xl md:w-auto"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            Annual ESG
          </motion.div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-6 md:space-x-8">
          {navItems.map((item, i) => (
            <motion.div
              key={i}
              className="relative"
              onMouseEnter={() => item.subItems && setHoveredItem(item.name)}
              onMouseLeave={() => item.subItems && setHoveredItem(null)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 * i }}
            >
              <Link
                href={item.href}
                className="text-gray-800 hover:text-gray-600 font-medium transition-colors relative group flex items-center gap-1"
              >
                {item.name}
                {item.subItems && (
                  <ChevronDown size={16} className="transition-transform duration-300 group-hover:rotate-180" />
                )}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gray-800 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <AnimatePresence>
                {item.subItems && hoveredItem === item.name && (
                  <motion.div
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="absolute top-full -translate-x-1/2 mt-5 w-56 bg-white rounded-lg shadow-xl p-2"
                  >
                    <div className="flex flex-col space-y-1">
                      {item.subItems.map((subItem, subIndex) => (
                        <Link
                          key={subIndex}
                          href={subItem.href}
                          className="text-gray-700 hover:bg-gray-100 px-4 py-2 rounded-md text-sm transition-colors"
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </nav>

        <motion.div
          className="hidden lg:block"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <Link
            href="#contact"
            className="bg-gray-800 hover:bg-gray-900 text-white px-5 py-3 rounded-full text-sm font-medium transition-all duration-300 hover:shadow-lg"
          >
            Get a Quote
          </Link>
        </motion.div>

        {/* Mobile Menu Button */}
        <motion.button
          className="lg:hidden text-gray-800 z-20 menu-button"
          whileTap={{ scale: 0.95 }}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </motion.button>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="lg:hidden bg-white fixed top-0 left-0 right-0 bottom-0 z-10 mobile-menu flex flex-col pt-12 sm:pt-20 overflow-y-auto"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="container mx-auto bg-white px-4 py-6 flex flex-col space-y-2">
              {navItems.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  {item.subItems ? (
                    <div>
                      <button
                        onClick={() => setOpenMobileSubMenu(openMobileSubMenu === item.name ? null : item.name)}
                        className="w-full flex justify-between items-center text-gray-800 hover:text-gray-600 font-medium transition-colors py-2 text-lg"
                      >
                        <span>{item.name}</span>
                        <ChevronDown
                          size={20}
                          className={`transition-transform duration-300 ${openMobileSubMenu === item.name ? "rotate-180" : ""}`}
                        />
                      </button>
                      <AnimatePresence>
                        {openMobileSubMenu === item.name && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden pl-4"
                          >
                            <div className="flex flex-col space-y-2 py-2">
                              {item.subItems.map((subItem, subIndex) => (
                                <Link
                                  key={subIndex}
                                  href={subItem.href}
                                  className="text-gray-700 hover:text-gray-600 py-2 block text-base"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  {subItem.name}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      href={item.href}
                      className="text-gray-800 hover:text-gray-600 font-medium transition-colors py-2 block text-lg"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  )}
                  <div className="h-px bg-gray-100 mt-2"></div>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="pt-4"
              >
                <button className="bg-gray-800 hover:bg-gray-900 text-white w-full py-3 rounded-lg font-medium">
                  Get a Quote
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  )
}
